import { API_CONFIG } from "config/api.config";
import PayloadModel from "model/_payload/payload.schema";
import { ContentModel } from "model/contents/content.schema";
import { ContentsModel } from "model/contents/contents.schema";
import { ProdRecomModel } from "model/contents/prod-recom.schema";
import { BrandRecomModel } from "model/contents/brand-recom.schema";
import { createBaseApi } from "../baseApi";

export const contentsApi = createBaseApi("content", "/").injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // Get Contents
    getContents: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "contents",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["Contents"],
      transformResponse: (response) => {
        return ContentsModel.jsonToModel(response);
      },
    }),

    // Get Content By Id
    getContentById: builder.query({
      query: (id) => {
        return {
          url: `contents/${id}`,
          method: "GET",
        };
      },
      providesTags: ["Content"],
      transformResponse: (response) => {
        return ContentModel.jsonToModel(response.data);
      },
    }),

    // Add Content
    addContent: builder.mutation({
      query: (payload) => {
        return {
          url: "contents",
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["Contents"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Update Content
    updateContent: builder.mutation({
      query: (payload) => ({
        url: `contents/${payload.id}`,
        method: "PATCH",
        body: payload.formData,
      }),
      invalidatesTags: ["Contents", "Content"],
      transformResponse: (response) => ContentModel.jsonToModel(response.data),
    }),

    // Delete Contents
    deleteContent: builder.mutation({
      query: (id) => {
        return {
          url: `contents/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Contents"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Get Product Recommendation Highlight List
    getProdRecomHighlight: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "products",
          method: "GET",
          params: { ...payload, is_recommended: true },
        };
      },
      providesTags: ["ProdRecomHighlight"],
      transformResponse: (response) => {
        return ProdRecomModel.jsonToModel(response);
      },
    }),

    // Get Product Recommendation Nomaroi List
    getProdRecomNomaroi: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "products",
          method: "GET",
          params: { ...payload, is_recommended: false },
        };
      },
      providesTags: ["ProdRecomNomaroi"],
      transformResponse: (response) => {
        return ProdRecomModel.jsonToModel(response);
      },
    }),

    // Add Product Recommendation to Highlight
    addProdRecomToHighlight: builder.mutation({
      query: (value) => {
        const products = Array.isArray(value) ? value : [value];
        console.log(products);
        return {
          url: "products/recommend",
          method: "PATCH",
          body: { ids: products.map((item) => item.id), is_recommended: true },
        };
      },
      invalidatesTags: ["ProdRecomHighlight", "ProdRecomNomaroi"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Delete Product Recommendation from Highlight
    deleteProdRecomHighlight: builder.mutation({
      query: (value) => {
        const products = Array.isArray(value) ? value : [value];
        return {
          url: "products/recommend",
          method: "PATCH",
          body: { ids: products.map((item) => item.id), is_recommended: false },
        };
      },
      invalidatesTags: ["ProdRecomHighlight", "ProdRecomNomaroi"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Get Brand Recommendation Highlight List
    getBrandRecomHighlight: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "products/brands",
          method: "GET",
          params: { ...payload, is_recommended: true },
        };
      },
      providesTags: ["BrandRecomHighlight"],
      transformResponse: (response) => {
        return BrandRecomModel.jsonToModel(response);
      },
    }),

    // Get Brand Recommendation Nomaroi List
    getBrandRecomNomaroi: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "products/brands",
          method: "GET",
          params: { ...payload, is_recommended: false },
        };
      },
      providesTags: ["BrandRecomNomaroi"],
      transformResponse: (response) => {
        return BrandRecomModel.jsonToModel(response);
      },
    }),

    // Add Brand Recommendation to Highlight
    addBrandRecomToHighlight: builder.mutation({
      query: (value) => {
        const brands = Array.isArray(value) ? value : [value];
        return {
          url: "products/brands/recommend",
          method: "PATCH",
          body: { ids: brands.map((item) => item.id), is_recommended: true },
        };
      },
      invalidatesTags: ["BrandRecomHighlight", "BrandRecomNomaroi"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Delete Brand Recommendation from Highlight
    deleteBrandRecomHighlight: builder.mutation({
      query: (value) => {
        const brands = Array.isArray(value) ? value : [value];
        return {
          url: "products/brands/recommend",
          method: "PATCH",
          body: { ids: brands.map((item) => item.id), is_recommended: false },
        };
      },
      invalidatesTags: ["BrandRecomHighlight", "BrandRecomNomaroi"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Update Brand Recommendation Highlight Order
    reorderBrandRecomHighlight: builder.mutation({
      query: (value) => {
        return {
          url: "products/brands/order",
          method: "PUT",
          body: value,
        };
      },
      transformResponse: (response) => {
        return response;
      },
    }),
  }),
});

export const {
  //
  useGetContentsQuery,
  useGetContentByIdQuery,
  useAddContentMutation,
  useUpdateContentMutation,
  useDeleteContentMutation,
  useGetProdRecomHighlightQuery,
  useDeleteProdRecomHighlightMutation,
  useAddProdRecomToHighlightMutation,
  useGetProdRecomNomaroiQuery,
  useGetBrandRecomHighlightQuery,
  useDeleteBrandRecomHighlightMutation,
  useAddBrandRecomToHighlightMutation,
  useReorderBrandRecomHighlightMutation,
  useGetBrandRecomNomaroiQuery,
} = contentsApi;
